/**
 * Pick A Card — popup UI.
 *
 * Owns the <pick-a-card> element: eligibility gating, the card game, the
 * capture form, and analytics. All cart and persisted-state work goes through
 * window.THHPickACard, defined in assets/pick-a-card-cart.js, which loads
 * from the layout head so it is always defined before this file runs.
 */
(function () {
  // ---- module helpers (Tasks 10 and 11 append here) ----

  function cartApi() {
    return window.THHPickACard || null;
  }

  function parseLines(value) {
    return String(value || '').split('\n')
      .map(function (line) { return line.trim(); })
      .filter(function (line) { return line !== ''; });
  }

  /** Handles are compared case-insensitively; CSS selectors must not be. */
  function parseHandles(value) {
    return parseLines(value).map(function (line) { return line.toLowerCase(); });
  }

  // Placeholders are [square brackets], NOT {{ curly }}. In a Shopify text
  // setting, {{ ... }} is the theme editor's dynamic-source syntax: it tries
  // to resolve the name as a Liquid binding and rejects the setting with
  // "Dynamic source 'n' is invalid." Square brackets are inert to Liquid.
  //
  // formatMoney below is the deliberate exception — it consumes Shopify's own
  // money_format, which genuinely is {{amount}}.
  function fillTemplate(template, values) {
    return String(template || '').replace(/\[(\w+)\]/g, function (match, key) {
      return Object.prototype.hasOwnProperty.call(values, key) ? values[key] : match;
    });
  }

  /**
   * Scroll lock.
   *
   * `body { overflow: hidden }` alone is not enough: iOS Safari keeps
   * scrolling the page behind a fixed overlay, and some themes scroll the
   * documentElement rather than the body. Pinning the body with position:fixed
   * at a negative top is the technique that actually holds everywhere — the
   * offset has to be restored on release or the page jumps to the top.
   *
   * Reference-counted so a second open cannot overwrite the stored offset with
   * 0 and lose the visitor's place.
   */
  var scrollLockCount = 0;
  var scrollLockY = 0;

  function lockScroll() {
    scrollLockCount += 1;
    if (scrollLockCount > 1) return;

    scrollLockY = window.pageYOffset || document.documentElement.scrollTop || 0;
    var body = document.body;
    body.style.position = 'fixed';
    body.style.top = '-' + scrollLockY + 'px';
    body.style.left = '0';
    body.style.right = '0';
    body.style.width = '100%';
    body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';
  }

  function unlockScroll() {
    if (scrollLockCount === 0) return;
    scrollLockCount -= 1;
    if (scrollLockCount > 0) return;

    var body = document.body;
    body.style.position = '';
    body.style.top = '';
    body.style.left = '';
    body.style.right = '';
    body.style.width = '';
    body.style.overflow = '';
    document.documentElement.style.overflow = '';
    window.scrollTo(0, scrollLockY);
  }

  function formatMoney(cents, format) {
    var amount = (cents / 100).toFixed(2);
    return String(format || '${{amount}}').replace(/\{\{\s*amount[^}]*\}\}/, amount);
  }

  /**
   * The age gate is absolute — ?pickacard=1 does NOT bypass it.
   * Probe the AgeCheck island, not the section wrapper: age-check.liquid:1 is
   * guarded on `enable` and its schema sets "class": "contents", so an empty
   * .shopify-section wrapper renders even when the gate is off. Probing the
   * wrapper would suppress this popup forever, silently.
   */
  function ageGatePassed() {
    var island = document.querySelector('[x-data*="AgeCheck"]');
    if (!island) return true;                       // gate not live

    var section = island.closest('.shopify-section');
    if (!section || !section.id) return false;      // live but unreadable: fail closed
    var key = 'switch-age-check-' + section.id.replace('shopify-section-', '');

    var approved = false;
    try {
      var raw = localStorage.getItem(key);
      if (raw) {
        var record = JSON.parse(raw);
        // Presence is NOT enough: setExpiringStorageItem (utils.js:498) writes
        // { value, expires } and the key lingers after expiry until the
        // theme's own getter removes it.
        approved = !!record && record.value === 'approved' && record.expires > Date.now();
      }
    } catch (e) {
      approved = false;
    }
    if (!approved) return false;

    try {
      var store = window.Alpine && window.Alpine.store && window.Alpine.store('modals');
      if (store && store.isOpen && store.isOpen('ageCheck')) return false;
    } catch (e) {}

    return true;
  }

  function isVisible(el) {
    if (!el || !el.offsetParent) return false;
    var rect = el.getBoundingClientRect();
    return rect.width > 0 && rect.height > 0;
  }

  function blockingOverlayVisible(selectors) {
    var blocked = selectors.some(function (selector) {
      try { return isVisible(document.querySelector(selector)); }
      catch (e) { return false; }        // a bad selector must not throw
    });
    if (blocked) return true;

    // The spec also defers to any open theme modal, not just the app list.
    // The store's real API is `open`, `close`, `closeAll`, `setUpHide`,
    // `isRegistered`, `isOpen` (assets/global.bundle.js:763-772) — there is no
    // `anyOpen`, and `popup` is not a registered name. Registered names are
    // ageCheck, nav, filters, sort, and cart. Iterate the map rather than
    // hardcoding, so a name added by a theme update is picked up for free.
    try {
      var store = window.Alpine && window.Alpine.store && window.Alpine.store('modals');
      if (store && store.modals && store.isOpen) {
        var names = Object.keys(store.modals);
        for (var i = 0; i < names.length; i++) {
          if (store.isOpen(names[i])) return true;
        }
      }
    } catch (e) {}
    return false;
  }

  function targeted(config) {
    var handle = String(config.currentHandle || '').toLowerCase();
    if (handle && parseHandles(config.excludeHandles).indexOf(handle) !== -1) return false;
    var include = parseHandles(config.includeHandles);
    if (include.length && handle && include.indexOf(handle) !== -1) return true;
    return config.templates[config.currentTemplate] === true;
  }

  function withinFrequencyCap(config, state) {
    if (!state.status || !state.playedAt) return true;
    var days = state.status === 'won'
      ? config.daysUntilReshowWon
      : config.daysUntilReshowDismissed;
    return Date.now() - state.playedAt > days * 24 * 60 * 60 * 1000;
  }

  function EligibilityGate(config, onOpen) {
    this.config = config;
    this.onOpen = onOpen;
    this.forced = /[?&]pickacard=1(&|$)/.test(window.location.search);
    this.retries = 0;
    this.timers = [];
    this.unbinders = [];
  }

  EligibilityGate.prototype.start = function () {
    if (!ageGatePassed()) return;                    // absolute, forced included
    if (window.Shopify && window.Shopify.designMode) return;

    this.selectors = parseLines(this.config.blockingSelectors);

    if (this.forced) {
      this.attempt();                                // no delay: ad and QA traffic
      return;
    }

    var state = (cartApi() && cartApi().state.get()) || {};
    if (!targeted(this.config)) return;
    if (!withinFrequencyCap(this.config, state)) return;

    this.armTriggers();
  };

  EligibilityGate.prototype.armTriggers = function () {
    var gate = this;

    var timer = setTimeout(function () { gate.attempt(); },
      Math.max(0, Number(this.config.delaySeconds) || 0) * 1000);
    this.timers.push(timer);

    if (!this.config.enableExitIntent) return;

    // Desktop: cursor leaves through the top of the viewport.
    var onMouseOut = function (event) {
      if (event.clientY <= 0 && !event.relatedTarget) gate.attempt();
    };
    document.addEventListener('mouseout', onMouseOut);
    this.unbinders.push(function () {
      document.removeEventListener('mouseout', onMouseOut);
    });

    // Touch: a fast upward flick after a quarter of the page.
    var lastY = window.scrollY;
    var lastT = Date.now();
    var onScroll = function () {
      var y = window.scrollY;
      var now = Date.now();
      var dt = now - lastT;
      if (dt >= 50) {                                  // sample no faster than 20Hz
        var velocity = (lastY - y) / (dt / 1000);      // positive = scrolling up
        var depth = y / Math.max(1, document.body.scrollHeight - window.innerHeight);
        if (velocity > 300 && depth > 0.25) gate.attempt();
        lastY = y;
        lastT = now;
      }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    this.unbinders.push(function () {
      window.removeEventListener('scroll', onScroll);
    });
  };

  /** Whichever trigger fires first wins; the rest are torn down. */
  EligibilityGate.prototype.unbind = function () {
    this.timers.forEach(clearTimeout);
    this.timers = [];
    this.unbinders.forEach(function (fn) { fn(); });
    this.unbinders = [];
  };

  EligibilityGate.prototype.attempt = function () {
    this.unbind();
    if (!this.forced && blockingOverlayVisible(this.selectors)) {
      if (this.retries >= 5) return;                   // give up for this page view
      this.retries += 1;
      var gate = this;
      setTimeout(function () { gate.attempt(); }, 2000);
      return;
    }
    this.onOpen();
  };

  /**
   * Rigged always-win. Prizes are drawn up front, without replacement, and
   * deduped by resolved variant so the same gift cannot be won twice. Which
   * card the visitor taps is theatre; the outcome is fixed before the first
   * flip. At most one discount code — a product decision, not a platform
   * limit — with gifts backfilling the rest.
   */
  function draw(prizes, picksCount) {
    var pool = prizes.slice();
    for (var i = pool.length - 1; i > 0; i--) {
      var j = Math.floor(Math.random() * (i + 1));
      var swap = pool[i]; pool[i] = pool[j]; pool[j] = swap;
    }

    var picked = [];
    var seenVariants = {};
    var discountTaken = false;

    for (var k = 0; k < pool.length && picked.length < picksCount; k++) {
      var prize = pool[k];
      if (prize.type === 'discount_code') {
        if (discountTaken) continue;
        discountTaken = true;
      } else if (prize.variantId) {
        // Only dedupe on a REAL variant id. Keying on a falsy one made the
        // first such prize claim seenVariants[0] and silently drop every
        // later prize that also lacked a variant — which is exactly what the
        // theme-editor placeholders are.
        if (seenVariants[prize.variantId]) continue;
        seenVariants[prize.variantId] = true;
      }
      picked.push(prize);
    }
    return picked;
  }

  function subscribe(config, email, phone) {
    if (!config.klaviyoCompanyId || !config.klaviyoListId) return Promise.resolve(false);

    var attributes = { email: email };
    if (phone) {
      attributes.phone_number = phone;
      // phone_number alone does NOT create SMS consent. Without this block
      // the number is stored and never subscribed.
      attributes.subscriptions = { sms: { marketing: { consent: 'SUBSCRIBED' } } };
    }

    var request = fetch('https://a.klaviyo.com/client/subscriptions/?company_id=' +
        encodeURIComponent(config.klaviyoCompanyId), {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        revision: '2024-10-15'                  // required; omitting it 400s
      },
      body: JSON.stringify({
        data: {
          type: 'subscription',
          attributes: {
            profile: { data: { type: 'profile', attributes: attributes } }
          },
          relationships: { list: { data: { type: 'list', id: config.klaviyoListId } } }
        }
      })
    }).then(function (r) { return r.ok; }).catch(function () { return false; });

    return Promise.race([
      request,
      new Promise(function (resolve) { setTimeout(function () { resolve(false); }, 5000); })
    ]);
  }

  /** Klaviyo requires E.164. Returns null when it cannot be normalised. */
  function toE164(raw) {
    var digits = String(raw || '').replace(/[^\d+]/g, '');
    if (/^\+\d{8,15}$/.test(digits)) return digits;
    if (/^\d{10}$/.test(digits)) return '+1' + digits;      // US default
    if (/^1\d{10}$/.test(digits)) return '+' + digits;
    return null;
  }

  function track(step, el) {
    // Clarity is the funnel sink: the same tag set at each step makes every
    // drop-off point filterable in session recordings.
    try { if (window.clarity) window.clarity('set', 'pick_a_card', step); } catch (e) {}

    // Klaviyo gets terminal steps only — these are profile events that drive
    // flows, and one per card tap would be noise.
    if (step === 'claim' || step === 'skip' || step === 'dismiss') {
      try {
        if (window._learnq) {
          window._learnq.push(['track', 'Played Pick A Card', {
            step: step,
            prizes: ((el && el.prizes) || []).map(function (p) { return p.label; }),
            template: el && el.config ? el.config.currentTemplate : null
          }]);
        }
      } catch (e) {}
    }

    // A shared payload, so every sink reports the same thing and numbers can
    // be reconciled across them.
    var payload = {
      pac_step: step,
      pac_prizes: ((el && el.prizes) || []).map(function (p) { return p.label; }).join(' | '),
      pac_prize_count: ((el && el.prizes) || []).length,
      pac_template: el && el.config ? el.config.currentTemplate : null
    };

    // GA4 via gtag, when the store has it directly rather than through a tag
    // manager. Event names are snake_case because GA4 requires it.
    try {
      if (typeof window.gtag === 'function') {
        window.gtag('event', 'pick_a_card_' + step, payload);
      }
    } catch (e) {}

    // Tag-manager path. Carries the same payload rather than a bare event
    // name, so a trigger can segment on prize or template without extra work.
    try {
      if (window.dataLayer && typeof window.dataLayer.push === 'function') {
        var dl = { event: 'pick_a_card_' + step };
        for (var key in payload) {
          if (Object.prototype.hasOwnProperty.call(payload, key)) dl[key] = payload[key];
        }
        window.dataLayer.push(dl);
      }
    } catch (e) {}

    // Triple Whale custom event, terminal steps only — it is an attribution
    // tool, and a card tap is not an outcome worth attributing.
    try {
      if (typeof window.TriplePixel === 'function' &&
          (step === 'claim' || step === 'skip')) {
        window.TriplePixel('Custom', { event: 'PickACard', step: step });
      }
    } catch (e) {}
  }

  // ---- element ----

  if (!customElements.get('pick-a-card')) {
    class PickACard extends HTMLElement {
      connectedCallback() {
        if (this.initialized) return;
        this.initialized = true;

        this.config = this.readConfig();
        if (!this.config) return;

        this.api = cartApi();
        if (!this.api) {
          // The cart layer failed to load. Do not open a game whose prizes
          // could never be applied.
          if (window.console) console.warn('[pick-a-card] cart layer missing; popup disabled');
          return;
        }
        this.api.init({ minimumSubtotalCents: this.config.minimumSubtotalCents });

        // Registered from the full prize list, not just the drawn ones, and
        // before any gating below. A prize granted in an earlier session still
        // needs its rule loaded now so the cart layer can withdraw it if
        // another app has since added the real product.
        if (typeof this.api.registerSupersessions === 'function') {
          this.api.registerSupersessions(
            (this.config.prizes || [])
              .filter(function (p) { return p.variantId && p.supersededBy; })
              .map(function (p) {
                return { gift: p.variantId, supersededBy: p.supersededBy };
              })
          );
        }

        this.liveRegion = this.querySelector('[data-pac-live]');
        this.titleEl = this.querySelector('.pick-a-card__title');
        // Captured from the rendered markup, so it stays in step with the
        // headline setting without being passed through the config twice.
        this.baseHeadline = this.titleEl ? this.titleEl.textContent.trim() : '';
        this.counter = this.querySelector('[data-pac-counter]');
        this.lastFocused = null;
        this.prizes = [];
        this.revealedCount = 0;

        this.bindChrome();

        // The `enable` checkbox is authoritative. Without this the popup would
        // open as soon as the section is added to the group, ignoring the
        // toggle, and the section could not ship dark.
        // Theme editor: bypass the gate entirely and drive the popup from the
        // editor's own selection events, so it can be styled and previewed.
        // Deliberately ignores `enabled`, targeting and the frequency cap —
        // none of which a merchant wants fighting them mid-edit.
        if (this.config.designMode) {
          this.bindThemeEditor();
          return;
        }

        if (!this.config.enabled) return;
        if (this.config.prizes.length === 0) return;

        this.gate = new EligibilityGate(this.config, this.open.bind(this));
        this.gate.start();
      }

      /**
       * Shopify fires these on the section wrapper when the merchant selects
       * or deselects the section in the editor sidebar. Clicking the section
       * shows the popup; clicking away hides it.
       */
      bindThemeEditor() {
        var el = this;
        var mine = function (event) {
          return !el.config.sectionId || event.detail.sectionId === el.config.sectionId;
        };

        document.addEventListener('shopify:section:select', function (event) {
          if (mine(event)) el.openForPreview();
        });
        document.addEventListener('shopify:section:deselect', function (event) {
          if (mine(event)) el.close();
        });
        document.addEventListener('shopify:block:select', function (event) {
          if (mine(event)) el.openForPreview();
        });

        // Open straight away so the section is visible the moment the editor
        // loads with it selected, rather than only after a click.
        this.openForPreview();
      }

      /** Open without the gate, and re-draw so setting edits take effect. */
      openForPreview() {
        if (this.dataset.state !== 'idle') return;
        this.open();
      }

      readConfig() {
        var node = this.querySelector('[data-pac-config]');
        if (!node) return null;
        try {
          return JSON.parse(node.textContent);
        } catch (e) {
          if (window.console) console.warn('[pick-a-card] bad config JSON', e);
          return null;
        }
      }

      setState(state) {
        this.dataset.state = state;
        this.syncTitle(state);
      }

      /**
       * The main heading changes with the step — "Pick a card, any card" while
       * picking, "Congratulations!" once they have won.
       *
       * Swapped in JS rather than rendered as several hidden headings so the
       * dialog keeps exactly one h2 and one aria-labelledby target. Multiple
       * headings toggled by CSS would leave a screen reader announcing the
       * label of a heading that is not on screen.
       *
       * An empty setting falls back to the base headline, so a merchant who
       * wants one heading throughout just clears the others.
       */
      syncTitle(state) {
        if (!this.titleEl) return;
        var headlines = this.config.headlines || {};
        var next = headlines[state];
        this.titleEl.textContent = (next && next !== '') ? next : this.baseHeadline;
      }

      announce(message) {
        if (this.liveRegion) this.liveRegion.textContent = message;
      }

      open() {
        if (this.dataset.state !== 'idle') return;

        this.prizes = draw(this.config.prizes, this.config.picksCount);
        if (!this.prizes.length) return;

        this.revealedCount = 0;
        this.lastFocused = document.activeElement;
        this.hidden = false;
        this.setState('intro');
        lockScroll();
        this.updateCounter();

        var first = this.querySelector('[data-pac-card]');
        if (first) first.focus();
        track('open', this);
      }

      /**
       * No cart mutation happens before `claimed`. The claim or skip action is
       * the consent moment, so closing at any earlier state applies nothing,
       * forfeits the revealed prizes, and records a dismissal.
       */
      close() {
        if (this.dataset.state === 'idle') return;
        var claimed = this.dataset.state === 'claimed';

        this.hidden = true;
        this.setState('idle');
        unlockScroll();
        if (this.lastFocused && this.lastFocused.focus) this.lastFocused.focus();

        if (claimed) return;

        // The theme editor opens and closes this repeatedly as the merchant
        // clicks around. Recording a dismissal each time would burn their own
        // frequency cap and fire junk analytics.
        if (this.config.designMode) return;

        // A ?pickacard=1 replay that ends in a dismissal must not downgrade an
        // earlier win from a 90-day suppression to a 7-day one.
        if (this.api.state.get().status !== 'won') {
          this.api.state.patch({ status: 'dismissed', playedAt: Date.now() });
        }
        track('dismiss', this);
      }

      /**
       * Close the funnel.
       *
       * `dismiss` only fires when someone actively closes the popup — button,
       * Escape or scrim. Leaving the page with it still open reported nothing,
       * so opens minus claims never equalled dismisses and the dismiss rate
       * flattered itself. This reports those as `abandon` instead.
       *
       * pagehide rather than beforeunload: beforeunload is unreliable on
       * mobile Safari, where a backgrounded tab is often simply discarded.
       */
      bindAbandon() {
        var el = this;
        var reported = false;

        var report = function () {
          if (reported) return;
          var state = el.dataset.state;
          if (state === 'idle' || state === 'claimed') return;   // resolved already
          if (el.config.designMode) return;
          reported = true;
          track('abandon', el);
        };

        window.addEventListener('pagehide', report);
        document.addEventListener('visibilitychange', function () {
          if (document.hidden) report();
        });
      }

      bindChrome() {
        var el = this;
        this.bindAbandon();

        var closer = this.querySelector('[data-pac-close]');
        if (closer) closer.addEventListener('click', function () { el.close(); });

        var scrim = this.querySelector('[data-pac-scrim]');
        if (scrim) scrim.addEventListener('click', function () { el.close(); });

        this.addEventListener('keydown', function (event) {
          if (event.key === 'Escape') {
            event.preventDefault();
            el.close();
            return;
          }
          if (event.key === 'Tab') el.trapTab(event);
        });

        this.bindCards();
        this.bindFlow();
      }

      focusables() {
        var selector = 'button, [href], input, select, textarea, [tabindex]';
        return Array.prototype.filter.call(this.querySelectorAll(selector), function (node) {
          // tabIndex >= 0 is load-bearing, not belt-and-braces. The cards are
          // <button>, so a bare `button` term matches them even while their
          // roving tabindex is -1. In intro/playing the only tabbable nodes
          // are the close button and card 0, but an unfiltered list ends at a
          // non-tabbable card — so `activeElement === last` never becomes
          // true, Tab is never intercepted, and focus escapes the dialog into
          // the page behind it.
          return node.offsetParent !== null && !node.disabled && node.tabIndex >= 0;
        });
      }

      trapTab(event) {
        var nodes = this.focusables();
        if (!nodes.length) return;
        var first = nodes[0];
        var last = nodes[nodes.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        }
      }

      bindCards() {
        var el = this;
        this.cards = Array.prototype.slice.call(this.querySelectorAll('[data-pac-card]'));
        this.cards.forEach(function (card, index) {
          card.addEventListener('click', function () { el.revealAt(index); });
          card.addEventListener('keydown', function (event) { el.onCardKey(event, index); });
        });
      }

      revealAt(index) {
        var card = this.cards[index];
        if (!card || card.hasAttribute('data-revealed') || card.hasAttribute('data-spent')) return;
        if (this.revealedCount >= this.prizes.length) return;

        if (this.dataset.state === 'intro') {
          this.setState('playing');
          track('play', this);
        }

        var prize = this.prizes[this.revealedCount];
        this.revealedCount += 1;

        var face = card.querySelector('[data-pac-card-face]');
        if (face) {
          face.textContent = '';
          if (prize.image) {
            var img = document.createElement('img');
            img.src = prize.image;
            img.alt = '';
            img.loading = 'lazy';
            face.appendChild(img);
          }
          var label = document.createElement('span');
          label.className = 'pick-a-card__card-label';
          label.textContent = prize.label;
          face.appendChild(label);

          if (prize.blurb) {
            var blurb = document.createElement('span');
            blurb.className = 'pick-a-card__card-blurb';
            blurb.textContent = prize.blurb;
            face.appendChild(blurb);
          }
        }
        card.setAttribute('data-revealed', 'true');
        card.setAttribute('aria-label', prize.label);

        this.updateCounter();
        this.announce(fillTemplate(this.config.copy.wonAnnouncement, {
          label: prize.label,
          n: this.revealedCount,
          total: this.prizes.length
        }));
        track('reveal', this);

        if (this.revealedCount >= this.prizes.length) this.finishReveal();
      }

      finishReveal() {
        var el = this;
        this.cards.forEach(function (card) {
          if (!card.hasAttribute('data-revealed')) {
            card.setAttribute('data-spent', 'true');
            card.setAttribute('tabindex', '-1');
          }
        });
        this.fireConfetti();
        this.setState('revealed');
        var next = this.querySelector('[data-pac-continue]');
        if (next) next.focus();
      }

      updateCounter() {
        if (!this.counter) return;
        this.counter.textContent = fillTemplate(this.config.copy.counter, {
          n: this.revealedCount,
          total: this.prizes.length
        });
      }

      fireConfetti() {
        var host = document.createElement('div');
        host.className = 'pick-a-card__confetti';
        host.setAttribute('aria-hidden', 'true');
        for (var i = 0; i < 40; i++) {
          var piece = document.createElement('i');
          piece.style.left = Math.random() * 100 + '%';
          piece.style.animationDelay = (Math.random() * 0.6).toFixed(2) + 's';
          host.appendChild(piece);
        }
        var dialog = this.querySelector('.pick-a-card__dialog');
        (dialog || this).appendChild(host);
      }

      onCardKey(event, index) {
        var columns = this.config.gridColumns || 3;
        var delta = 0;
        if (event.key === 'ArrowRight') delta = 1;
        else if (event.key === 'ArrowLeft') delta = -1;
        else if (event.key === 'ArrowDown') delta = columns;
        else if (event.key === 'ArrowUp') delta = -columns;
        else return;

        event.preventDefault();

        // Step past spent cards rather than parking focus on a dead one after
        // the reveal ends. Falls back to staying put if nothing is reachable.
        var next = index;
        for (var i = 0; i < this.cards.length; i++) {
          var candidate = next + delta;
          if (candidate < 0 || candidate > this.cards.length - 1) break;
          next = candidate;
          if (!this.cards[next].hasAttribute('data-spent')) break;
        }
        if (this.cards[next].hasAttribute('data-spent')) return;

        this.cards.forEach(function (card) { card.setAttribute('tabindex', '-1'); });
        this.cards[next].setAttribute('tabindex', '0');
        this.cards[next].focus();
      }

      bindFlow() {
        var el = this;

        var next = this.querySelector('[data-pac-continue]');
        if (next) next.addEventListener('click', function () {
          el.setState('capture');
          var email = el.querySelector('[data-pac-email]');
          if (email) email.focus();
        });

        var form = this.querySelector('[data-pac-form]');
        if (form) form.addEventListener('submit', function (event) {
          event.preventDefault();
          el.submitCapture();
        });

        var skip = this.querySelector('[data-pac-skip]');
        if (skip) skip.addEventListener('click', function () { el.finish('skip'); });

        var copyBtn = this.querySelector('[data-pac-copy-code]');
        if (copyBtn) copyBtn.addEventListener('click', function () {
          var code = el.querySelector('[data-pac-outcome-code]');
          if (code && navigator.clipboard) navigator.clipboard.writeText(code.textContent);
        });
      }

      submitCapture() {
        var el = this;
        var emailInput = this.querySelector('[data-pac-email]');
        var phoneInput = this.querySelector('[data-pac-phone]');
        var consent = this.querySelector('[data-pac-sms-consent]');

        var email = emailInput ? emailInput.value.trim() : '';
        if (!email || email.indexOf('@') === -1) {
          return this.showError(emailInput && emailInput.validationMessage);
        }

        var phone = null;
        if (phoneInput && phoneInput.value.trim()) {
          if (consent && !consent.checked) {
            return this.showError(this.config.copy.consentRequired);
          }
          phone = toE164(phoneInput.value);
          if (!phone) return this.showError(this.config.copy.phoneInvalid);
        }

        this.showError(null);
        this.setBusy(true);

        subscribe(this.config, email, phone)
          .then(function (ok) {
            if (ok) track('subscribe', el);
            else el.showNotice(el.config.copy.subscribeFailed);
            return el.finish('claim');
          })
          .catch(function () {
            el.showNotice(el.config.copy.subscribeFailed);
            return el.finish('claim');
          });
      }

      finish(step) {
        var el = this;
        this.setBusy(true);

        // In the theme editor, never touch the real cart or persisted state.
        // A merchant clicking through the flow to preview the success panel
        // must not add $0.00 lines to their own cart or burn the frequency
        // cap. Show the claimed panel with the prizes marked applied so the
        // copy and layout can be checked, and stop there.
        if (this.config.designMode) {
          var previewIds = this.prizes
            .filter(function (p) { return p.type === 'free_gift'; })
            .map(function (p) { return p.variantId; });
          this.renderOutcome({
            applied: previewIds, reserved: [], failed: [],
            discountCode: (this.prizes.filter(function (p) { return p.type === 'discount_code'; })[0] || {}).code || null,
            discountApplied: false, paidSubtotalCents: 0, shortfallCents: 0
          });
          this.setState('claimed');
          this.setBusy(false);
          return Promise.resolve();
        }

        return this.api.applyRewards(this.prizes)
          .then(function (outcome) { el.completeWith(outcome, step); })
          .catch(function () {
            // Never leave the visitor on a spinner. Show the claimed panel
            // with nothing applied and let GiftGuard/deferred add reconcile.
            el.completeWith({ applied: [], reserved: [], failed: [],
              discountCode: null, discountApplied: false, shortfallCents: 0 }, step);
          });
      }

      completeWith(outcome, step) {
        this.api.state.patch({
          status: 'won',
          playedAt: Date.now(),
          prizes: this.prizes.map(function (p) { return { label: p.label, type: p.type }; })
        });
        this.renderOutcome(outcome);
        this.setState('claimed');
        this.setBusy(false);
        track(step, this);            // exactly one terminal step per session
      }

      showError(message) {
        var node = this.querySelector('[data-pac-error]');
        if (!node) return;
        node.textContent = message || '';
        node.hidden = !message;
      }

      showNotice(message) {
        var node = this.querySelector('[data-pac-notice]');
        if (!node) return;
        node.textContent = message || '';
        node.hidden = !message;
      }

      setBusy(busy) {
        var submit = this.querySelector('[data-pac-submit]');
        if (submit) submit.disabled = !!busy;
        this.toggleAttribute('data-busy', !!busy);
      }

      renderOutcome(outcome) {
        var el = this;
        var appliedIds = (outcome.applied || []).map(String);

        var list = this.querySelector('[data-pac-outcome-gifts]');
        if (list) {
          list.textContent = '';
          // List only what actually landed.
          this.prizes.forEach(function (prize) {
            if (prize.type !== 'free_gift') return;
            if (appliedIds.indexOf(String(prize.variantId)) === -1) return;
            var item = document.createElement('li');
            item.textContent = prize.label;
            list.appendChild(item);
          });
        }

        var reserved = this.querySelector('[data-pac-outcome-reserved]');
        if (reserved) {
          var isReserved = (outcome.reserved || []).length > 0;
          reserved.hidden = !isReserved;
          if (isReserved) {
            var copy = this.config.copy.reservedGifts;
            if (outcome.shortfallCents > 0) {
              copy += ' ' + fillTemplate(this.config.copy.shortfall, {
                amount: formatMoney(outcome.shortfallCents, this.config.moneyFormat)
              });
            }
            reserved.textContent = copy;
          }
        }

        var discountWrap = this.querySelector('[data-pac-outcome-discount]');
        if (discountWrap) {
          discountWrap.hidden = !outcome.discountCode;
          var code = this.querySelector('[data-pac-outcome-code]');
          if (code) code.textContent = outcome.discountCode || '';
        }
      }
    }

    customElements.define('pick-a-card', PickACard);
  }
})();
